// React-query hooks for pools, positions, chain time and staking mutations.

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PublicKey, Transaction } from '@solana/web3.js';
import { useConnection } from '../connection';
import { KNOWN_MINT_INFO, useWallet } from '../wallet';
import { useTokenAccounts } from '../tokens/hooks';
import { TOKEN_PROGRAM_ID } from '../tokens/instructions';
import { deserializeMetadataData, getMetadataPDA } from '../tokens/metadata';
import {
  PROGRAM_ID,
  Pool,
  Position,
  decodePool,
  decodePosition,
  mintDecimals,
  poolPda,
  positionPda,
  tokenAccountAmount,
} from './program';
import {
  MintHolding,
  buildClaim,
  buildStake,
  buildUnstake,
  holdingsForMint,
  sendStakingTransaction,
} from './transactions';

const REFETCH_MS = 30_000;

// Surface lookup failures instead of retrying silently forever (client default is retry: true).
const QUERY_OPTS = { retry: 2, onError: (err: unknown) => console.error('tokenStaking', err) };

export interface PoolInfo {
  pool: Pool;
  rewardVaultBalance: bigint;
  decimals: number;
}

export interface MintInfo {
  symbol: string;
  decimals: number;
  logoUri: string | null;
}

const key = (mint?: PublicKey | null) => mint?.toBase58() ?? null;
const trimMetadata = (value?: string | null) => value?.replace(/^[\s\uFEFF\xA0\0]+|[\s\uFEFF\xA0\0]+$/g, '') ?? '';

/** Pools for many mints in one RPC call; mints without a pool are absent. */
export function usePools(mints: PublicKey[]) {
  const connection = useConnection();
  const addresses = useMemo(
    () => Array.from(new Set(mints.map((mint) => mint.toBase58()))).sort(),
    [mints],
  );
  return useQuery<Record<string, Pool>>({
    ...QUERY_OPTS,
    queryKey: ['tokenStakingPools', connection.rpcEndpoint, addresses.join(',')],
    enabled: !!PROGRAM_ID && addresses.length > 0,
    refetchInterval: REFETCH_MS,
    queryFn: async () => {
      const accounts = await connection.getMultipleAccountsInfo(
        addresses.map((address) => poolPda(new PublicKey(address))),
      );
      const pools: Record<string, Pool> = {};
      accounts.forEach((account, index) => {
        if (account) pools[addresses[index]] = decodePool(account.data);
      });
      return pools;
    },
  });
}

/** Pool plus reward-vault balance and mint decimals. Null data when no pool exists. */
export function usePoolInfo(mint?: PublicKey | null) {
  const connection = useConnection();
  return useQuery<PoolInfo | null>({
    ...QUERY_OPTS,
    queryKey: ['tokenStakingPool', connection.rpcEndpoint, key(mint)],
    enabled: !!PROGRAM_ID && !!mint,
    refetchInterval: REFETCH_MS,
    queryFn: async () => {
      const account = await connection.getAccountInfo(poolPda(mint!));
      if (!account) return null;
      const pool = decodePool(account.data);
      const [vault, mintAccount] = await connection.getMultipleAccountsInfo([pool.rewardVault, pool.mint]);
      if (!vault || !mintAccount) throw new Error('pool accounts missing');
      return { pool, rewardVaultBalance: tokenAccountAmount(vault.data), decimals: mintDecimals(mintAccount.data) };
    },
  });
}

export function usePosition(mint?: PublicKey | null) {
  const connection = useConnection();
  const wallet = useWallet();
  return useQuery<Position | null>({
    ...QUERY_OPTS,
    queryKey: ['tokenStakingPosition', connection.rpcEndpoint, key(mint), wallet?.publicKey?.toBase58()],
    enabled: !!PROGRAM_ID && !!mint && !!wallet?.publicKey,
    refetchInterval: REFETCH_MS,
    queryFn: async () => {
      const account = await connection.getAccountInfo(positionPda(mint!, wallet.publicKey));
      return account ? decodePosition(account.data) : null;
    },
  });
}

/** Chain time in seconds, extrapolated locally between refreshes. */
export function useChainClock(): () => bigint {
  const connection = useConnection();
  const { data: offset } = useQuery<number>({
    ...QUERY_OPTS,
    queryKey: ['tokenStakingClock', connection.rpcEndpoint],
    refetchInterval: REFETCH_MS,
    queryFn: async () => {
      const time = await connection.getBlockTime(await connection.getSlot());
      return time === null ? 0 : time - Math.floor(Date.now() / 1000);
    },
  });
  return useCallback(() => BigInt(Math.floor(Date.now() / 1000) + (offset ?? 0)), [offset]);
}

/** Symbol, decimals and logo for mints, from token metadata and mint accounts. */
export function useMintInfos(mints: PublicKey[]) {
  const connection = useConnection();
  const addresses = useMemo(
    () => Array.from(new Set(mints.map((mint) => mint.toBase58()))).sort(),
    [mints],
  );
  return useQuery<Record<string, MintInfo>>({
    ...QUERY_OPTS,
    queryKey: ['tokenStakingMintInfos', connection.rpcEndpoint, addresses.join(',')],
    enabled: addresses.length > 0,
    staleTime: Infinity,
    queryFn: async () => {
      const keys = addresses.map((address) => new PublicKey(address));
      const accounts = await connection.getMultipleAccountsInfo([...keys, ...keys.map(getMetadataPDA)]);
      const infos: Record<string, MintInfo> = {};
      keys.forEach((mint, index) => {
        const mintAccount = accounts[index];
        if (!mintAccount) return;
        const address = mint.toBase58();
        const metadata = accounts[index + keys.length];
        const data = metadata ? deserializeMetadataData(metadata.data) : null;
        const known = KNOWN_MINT_INFO[address];
        infos[address] = {
          symbol: trimMetadata(data?.symbol) || known?.symbol || `${address.slice(0, 4)}…`,
          decimals: mintDecimals(mintAccount.data),
          logoUri: trimMetadata(data?.uri) || known?.uri || null,
        };
      });
      return infos;
    },
  });
}

/** The wallet's spendable accounts for the pool's mint. */
export function useHoldings(pool?: Pool | null): MintHolding[] {
  const { data } = useTokenAccounts(pool?.tokenProgram ?? TOKEN_PROGRAM_ID);
  return useMemo(() => (pool ? holdingsForMint(data, pool.mint) : []), [data, pool]);
}

function useStakingMutation<TArgs>(
  mint: PublicKey | null | undefined,
  build: (args: TArgs) => Promise<Transaction>,
) {
  const connection = useConnection();
  const wallet = useWallet();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: TArgs) => sendStakingTransaction(connection, wallet, await build(args)),
    onSettled: () => {
      queryClient.invalidateQueries(['tokenAccounts']);
      queryClient.invalidateQueries(['tokenStakingPool', connection.rpcEndpoint, key(mint)]);
      queryClient.invalidateQueries(['tokenStakingPools']);
      queryClient.invalidateQueries(['tokenStakingPosition', connection.rpcEndpoint, key(mint)]);
    },
  });
}

export function useStake(pool?: Pool | null) {
  const wallet = useWallet();
  const holdings = useHoldings(pool);
  return useStakingMutation<bigint>(pool?.mint, async (amount) => buildStake(wallet.publicKey, pool!, holdings, amount));
}

export function useClaim(pool?: Pool | null) {
  const connection = useConnection();
  const wallet = useWallet();
  const holdings = useHoldings(pool);
  return useStakingMutation<void>(pool?.mint, async () => buildClaim(connection, wallet.publicKey, pool!, holdings));
}

export function useUnstake(pool?: Pool | null) {
  const connection = useConnection();
  const wallet = useWallet();
  const holdings = useHoldings(pool);
  return useStakingMutation<bigint | null>(pool?.mint, async (amount) =>
    buildUnstake(connection, wallet.publicKey, pool!, holdings, amount),
  );
}
